import { motion } from "framer-motion";
import { Mail, Globe, Instagram, Facebook, Twitter } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_1372e601-3756-4090-a4b3-dfd0e81eaf5a/artifacts/djc1777r_Muscle%20Mineral%20Logo.png";
const XTREME_LOGO_URL = "https://customer-assets.emergentagent.com/job_1372e601-3756-4090-a4b3-dfd0e81eaf5a/artifacts/mlrns2tl_image.png";
const BUY_URL = "https://xtremenutrition.co.za/product/muscle-mineral-creatine-monohydrate-1kg/";

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer 
      className="bg-[#050505] border-t border-white/10 py-16 md:py-20"
      data-testid="footer"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
        {/* Main footer content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand column */}
          <div className="lg:col-span-2">
            <img 
              src={LOGO_URL} 
              alt="Muscle Mineral" 
              className="h-12 w-auto invert mb-6"
              data-testid="footer-logo"
            />
            <p className="text-[#A3A3A3] leading-relaxed mb-6 max-w-md">
              Forged by the Elements. Backed by Science. Premium performance supplements for athletes who demand the best.
            </p>
            <a
              href={BUY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#3A3A3A] hover:bg-[#4A4A4A] text-white font-bold uppercase tracking-wider px-5 py-3 transition-all duration-300 hover:scale-105"
              style={{ fontFamily: 'Oswald, sans-serif' }}
              data-testid="footer-buy-btn"
            >
              <span className="text-sm">Buy at</span>
              <img 
                src={XTREME_LOGO_URL} 
                alt="Xtreme Nutrition" 
                className="h-7 w-auto"
              />
            </a>
          </div>

          {/* Quick links */}
          <div>
            <h4 
              className="text-lg font-bold uppercase tracking-tight text-white mb-6"
              style={{ fontFamily: 'Oswald, sans-serif' }}
            >
              Quick Links
            </h4>
            <nav className="space-y-3">
              <a href="#product" className="block text-[#A3A3A3] hover:text-white transition-colors" data-testid="footer-link-product">
                Product
              </a>
              <a href="#benefits" className="block text-[#A3A3A3] hover:text-white transition-colors" data-testid="footer-link-benefits">
                Benefits
              </a>
              <a href="#nutrition" className="block text-[#A3A3A3] hover:text-white transition-colors" data-testid="footer-link-nutrition">
                Nutrition
              </a>
              <a href="#about" className="block text-[#A3A3A3] hover:text-white transition-colors" data-testid="footer-link-about">
                About
              </a>
            </nav>
          </div>

          {/* Contact */}
          <div>
            <h4 
              className="text-lg font-bold uppercase tracking-tight text-white mb-6"
              style={{ fontFamily: 'Oswald, sans-serif' }}
            >
              Contact
            </h4>
            <div className="space-y-4">
              <a 
                href="mailto:strength@musclemineral.com" 
                className="flex items-center gap-3 text-[#A3A3A3] hover:text-white transition-colors"
                data-testid="footer-email"
              >
                <Mail size={18} strokeWidth={1.5} />
                <span>strength@musclemineral.com</span>
              </a>
              <a 
                href="https://musclemineral.com" 
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-[#A3A3A3] hover:text-white transition-colors"
                data-testid="footer-website"
              >
                <Globe size={18} strokeWidth={1.5} />
                <span>musclemineral.com</span>
              </a>
            </div>

            {/* Social links - Hidden until links are provided
            <div className="flex gap-4 mt-6">
              <a 
                href="#" 
                className="w-10 h-10 flex items-center justify-center border border-[#616556]/50 hover:border-[#E82D00] hover:text-[#E82D00] text-[#A3A3A3] transition-colors"
                data-testid="footer-social-instagram"
              >
                <Instagram size={18} strokeWidth={1.5} />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 flex items-center justify-center border border-[#616556]/50 hover:border-[#E82D00] hover:text-[#E82D00] text-[#A3A3A3] transition-colors"
                data-testid="footer-social-facebook"
              >
                <Facebook size={18} strokeWidth={1.5} />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 flex items-center justify-center border border-[#616556]/50 hover:border-[#E82D00] hover:text-[#E82D00] text-[#A3A3A3] transition-colors"
                data-testid="footer-social-twitter"
              >
                <Twitter size={18} strokeWidth={1.5} />
              </a>
            </div>
            */}
          </div>
        </div>

        {/* Large brand text */}
        <div className="border-t border-white/10 pt-12 mb-12 overflow-hidden">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="text-[8vw] md:text-[6vw] font-bold uppercase tracking-tighter text-white/5 whitespace-nowrap"
            style={{ fontFamily: 'Oswald, sans-serif' }}
            data-testid="footer-brand-text"
          >
            MUSCLE MINERAL
          </motion.p>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-8 border-t border-white/10">
          <p className="font-mono text-xs text-[#A3A3A3]" data-testid="footer-copyright">
            © {currentYear} Muscle Mineral™. All rights reserved.
          </p>
          <div className="flex gap-6">
            <a href="#" className="font-mono text-xs text-[#A3A3A3] hover:text-white transition-colors">
              Privacy Policy
            </a>
            <a href="#" className="font-mono text-xs text-[#A3A3A3] hover:text-white transition-colors">
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
